// Import Swiper styles
import 'swiper/css';
import 'swiper/css/zoom';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/scrollbar';

import { Backdrop, IosClose } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import { useEffect, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import {
  FreeMode,
  Mousewheel,
  Navigation,
  Scrollbar,
  Thumbs,
  Zoom,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ImageWithFallback } from '@/components/image-with-fallback';

import { SlideNextButton } from './slide-next-button';
import { SlidePrevButton } from './slide-prev-button';
import styles from './styles.module.scss';
import type { Component } from './types';

export const GalleryViewer: Component = ({
  isOpen,
  images,
  initialSideIndex = 0,
  onClose,
}) => {
  const [
    thumbsSwiper,
    setThumbsSwiper,
  ] = useState<SwiperType | null>(null);
  const [
    activeIndex,
    setActiveIndex,
  ] = useState(initialSideIndex);

  const handleBackdropClose = () => {
    setThumbsSwiper(null);
    onClose?.();
  };

  useEffect(() => {
    if (thumbsSwiper) {
      thumbsSwiper.slideTo(activeIndex);
    }
  }, [activeIndex, thumbsSwiper]);

  return images.length !== 0 && (
    <Backdrop
      className={cx(
        styles.containerBackdrop,
        styles.isDeeper,
      )}
      isOpen={isOpen}
      onClose={handleBackdropClose}>
      <button
        aria-label="Close"
        onClick={handleBackdropClose}>
        <IosClose
          className={styles.iconClose}
          size={18} />
      </button>
      <Swiper
        className={cx(
          styles.swiper,
          styles.containerSwiperLightbox,
        )}
        initialSlide={initialSideIndex}
        modules={[Navigation, Thumbs, Zoom]}
        navigation={{
          nextEl: `.${styles.buttonNext}`,
          prevEl: `.${styles.buttonPrev}`,
        }}
        spaceBetween={10}
        thumbs={{
          swiper: thumbsSwiper,
          slideThumbActiveClass: styles.thumbActive,
          autoScrollOffset: 1,
        }}
        zoom>
        {images.map(image => (
          <SwiperSlide
            key={`${image.url}`}
            className={styles.swiperSlide}>
            <ImageWithFallback
              alt={image.name}
              className={cx(
                styles.image,
                styles.hasRadiusBorder,
              )}
              fallback={`${image.url}.jpg`}
              height={0}
              src={`${image.url}_b.jpg`}
              width={0} />
          </SwiperSlide>
        ))}
        <SlideNextButton className={styles.buttonNext} />
        <SlidePrevButton className={styles.buttonPrev} />
      </Swiper>
      <Swiper
        className={cx(
          styles.swiper,
          styles.containerSwiperThumbnails,
        )}
        initialSlide={initialSideIndex}
        modules={[FreeMode, Navigation, Thumbs, Scrollbar, Mousewheel]}
        slideActiveClass={styles.thumbSlideActive}
        slidesPerView="auto"
        spaceBetween={8}
        freeMode
        mousewheel
        scrollbar
        onSlideChange={
          swiper => setActiveIndex(swiper.activeIndex)
        }
        onSwiper={swiper => setThumbsSwiper(swiper)}>
        {images.map(image => (
          <SwiperSlide
            key={`${image.url}`}
            className={styles.swiperSlide}>
            <ImageWithFallback
              alt={image.name}
              className={styles.hasRadiusBorder}
              fallback={`${image.url}.jpg`}
              height={79}
              src={`${image.url}_b.jpg`}
              width={105} />
          </SwiperSlide>
        ))}
      </Swiper>
      <span className={styles.textModelName}>
        {images[activeIndex].name}
      </span>
    </Backdrop>
  );
};
